import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

let quillPromise = null;

function loadQuill() {
  if (window.Quill) return Promise.resolve(window.Quill);
  if (quillPromise) return quillPromise;

  quillPromise = new Promise((resolve, reject) => {
    if (!document.querySelector('link[data-camp-quill="1"]')) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://cdn.jsdelivr.net/npm/quill@2.0.3/dist/quill.snow.css";
      link.dataset.campQuill = "1";
      document.head.appendChild(link);
    }

    const existing = document.querySelector('script[data-camp-quill="1"]');
    if (existing) {
      if (window.Quill) {
        resolve(window.Quill);
        return;
      }
      existing.addEventListener("load", () => resolve(window.Quill), { once: true });
      existing.addEventListener("error", reject, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://cdn.jsdelivr.net/npm/quill@2.0.3/dist/quill.js";
    script.async = true;
    script.dataset.campQuill = "1";
    script.onload = () => resolve(window.Quill);
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return quillPromise;
}

function registerFieldNoteBlock(Quill) {
  try {
    const existing = Quill.import("formats/fieldNoteBlock");
    if (existing) return;
  } catch (_) {
    // Not registered yet.
  }

  const BlockEmbed = Quill.import("blots/block/embed");

  class FieldNoteBlockBlot extends BlockEmbed {
    static create(value) {
      const node = super.create();
      const key = typeof value === "string" ? value : String(value?.key || "");
      node.setAttribute("data-block-key", key);
      node.setAttribute("contenteditable", "false");
      node.setAttribute("role", "button");
      node.setAttribute("tabindex", "0");
      node.setAttribute("aria-label", "Field Note content block");
      return node;
    }

    static value(node) {
      return node.getAttribute("data-block-key") || "";
    }
  }

  FieldNoteBlockBlot.blotName = "fieldNoteBlock";
  FieldNoteBlockBlot.tagName = "div";
  FieldNoteBlockBlot.className = "fieldnote-quill-block";

  Quill.register(FieldNoteBlockBlot, true);
}

function normaliseBlocks(blocks) {
  return Array.isArray(blocks) ? blocks : [];
}

function providerLabel(provider) {
  const map = {
    youtube: "YouTube",
    vimeo: "Vimeo",
    soundcloud: "SoundCloud",
    mixcloud: "Mixcloud",
    bandcamp: "Bandcamp",
  };
  return map[String(provider || "").toLowerCase()] || "Media";
}

function blockDisplayMap(blocks) {
  let artifactNumber = 0;
  let mediaNumber = 0;
  const map = new Map();

  normaliseBlocks(blocks).forEach((block) => {
    if (!block || !block.block_key) return;

    const data = block.data && typeof block.data === "object" ? block.data : {};
    if (block.block_type === "artifact") {
      artifactNumber += 1;
      const title = String(data.caption || data.original_name || "Attached file").trim();
      map.set(block.block_key, {
        kicker: `ARTIFACT ${artifactNumber}`,
        title,
        secondary: data.original_name && data.caption ? String(data.original_name) : "",
        imageUrl: data.is_image && data.url ? String(data.url) : "",
      });
      return;
    }

    mediaNumber += 1;
    const provider = providerLabel(data.provider);
    const title = String(data.caption || provider).trim();
    map.set(block.block_key, {
      kicker: `MEDIA ${mediaNumber} · ${provider}`,
      title,
      secondary: data.caption ? "" : String(data.url || ""),
      imageUrl: "",
    });
  });

  return map;
}

function renderBlockNode(node, metadata) {
  const meta = metadata || {
    kicker: "CONTENT BLOCK",
    title: "Block details unavailable",
    secondary: "",
    imageUrl: "",
  };

  node.innerHTML = "";
  node.setAttribute("aria-label", `${meta.kicker}: ${meta.title}. Click to edit details.`);

  const dragHandle = document.createElement("button");
  dragHandle.type = "button";
  dragHandle.className = "fieldnote-quill-block__drag-handle";
  dragHandle.setAttribute("aria-label", `Move ${meta.kicker}: ${meta.title}`);
  dragHandle.setAttribute("title", "Drag to move this block in the article");
  dragHandle.textContent = "⠿";
  node.appendChild(dragHandle);

  if (meta.imageUrl) {
    const image = document.createElement("img");
    image.className = "fieldnote-quill-block__thumb";
    image.src = meta.imageUrl;
    image.alt = "";
    node.appendChild(image);
  }

  const copy = document.createElement("span");
  copy.className = "fieldnote-quill-block__copy";

  const kicker = document.createElement("span");
  kicker.className = "fieldnote-quill-block__kicker";
  kicker.textContent = meta.kicker;
  copy.appendChild(kicker);

  const title = document.createElement("strong");
  title.className = "fieldnote-quill-block__title";
  title.textContent = meta.title;
  copy.appendChild(title);

  if (meta.secondary) {
    const secondary = document.createElement("span");
    secondary.className = "fieldnote-quill-block__secondary";
    secondary.textContent = meta.secondary;
    copy.appendChild(secondary);
  }

  const hint = document.createElement("span");
  hint.className = "fieldnote-quill-block__hint";
  hint.textContent = "Drag ⠿ to move · click to edit details";
  copy.appendChild(hint);

  node.appendChild(copy);
}

function syncBlockPreviews(quill, blocks) {
  if (!quill?.root) return;
  const display = blockDisplayMap(blocks);
  quill.root.querySelectorAll(".fieldnote-quill-block[data-block-key]").forEach((node) => {
    const key = node.getAttribute("data-block-key") || "";
    renderBlockNode(node, display.get(key));
  });
}

function getInsertedBlockKeys(quill) {
  if (!quill) return [];
  const keys = [];
  const seen = new Set();
  const ops = quill.getContents()?.ops || [];

  ops.forEach((op) => {
    const value = op?.insert?.fieldNoteBlock;
    const key = typeof value === "string" ? value : String(value?.key || "");
    if (key && !seen.has(key)) {
      seen.add(key);
      keys.push(key);
    }
  });

  return keys;
}

function canonicalHtml(quill) {
  if (!quill?.root) return "";
  const clone = quill.root.cloneNode(true);

  clone.querySelectorAll(".fieldnote-quill-block[data-block-key]").forEach((node) => {
    const key = node.getAttribute("data-block-key") || "";
    const marker = `[[FIELDNOTE_BLOCK:${key}]]`;

    if (node.parentElement === clone) {
      const paragraph = document.createElement("p");
      paragraph.textContent = marker;
      node.replaceWith(paragraph);
    } else {
      node.replaceWith(document.createTextNode(marker));
    }
  });

  return clone.innerHTML;
}

function upgradeStoredMarkers(quill) {
  const text = quill.getText();
  const regex = /\[\[FIELDNOTE_BLOCK:([A-Za-z0-9_-]+)\]\]/g;
  const matches = [];
  let match;

  while ((match = regex.exec(text)) !== null) {
    matches.push({ index: match.index, length: match[0].length, key: match[1] });
  }

  matches.reverse().forEach(({ index, length, key }) => {
    quill.deleteText(index, length, "silent");
    quill.insertEmbed(index, "fieldNoteBlock", key, "silent");
  });
}

function blockKeyFromInput(blockOrKey) {
  if (typeof blockOrKey === "string") return blockOrKey;
  return String(blockOrKey?.block_key || "");
}

function createDropIndicator(quill) {
  if (!quill?.container) return null;
  let indicator = quill.container.querySelector(":scope > .fieldnote-quill-drop-indicator");
  if (indicator) return indicator;

  indicator = document.createElement("div");
  indicator.className = "fieldnote-quill-drop-indicator";
  indicator.setAttribute("aria-hidden", "true");

  const label = document.createElement("span");
  label.textContent = "Move block here";
  indicator.appendChild(label);

  quill.container.appendChild(indicator);
  return indicator;
}

function removeDropIndicator(quill) {
  quill?.container
    ?.querySelectorAll(":scope > .fieldnote-quill-drop-indicator")
    .forEach((node) => node.remove());
}

function getDropTarget(Quill, quill, draggedNode, clientY) {
  if (!quill?.root) return null;

  const candidates = Array.from(quill.root.children).filter(
    (node) => node !== draggedNode && !node.classList.contains("ql-ui")
  );

  if (!candidates.length) {
    return { index: 0, viewportY: quill.root.getBoundingClientRect().top + 12 };
  }

  for (const node of candidates) {
    const rect = node.getBoundingClientRect();
    const midpoint = rect.top + rect.height / 2;
    if (clientY < midpoint) {
      const blot = Quill.find(node);
      if (!blot) continue;
      return {
        index: quill.getIndex(blot),
        viewportY: rect.top,
      };
    }
  }

  const last = candidates[candidates.length - 1];
  const lastRect = last.getBoundingClientRect();
  const lastBlot = Quill.find(last);
  if (!lastBlot) return null;

  return {
    index: quill.getIndex(lastBlot) + lastBlot.length(),
    viewportY: lastRect.bottom,
  };
}

function positionDropIndicator(quill, indicator, viewportY) {
  if (!quill?.container || !indicator) return;
  const containerRect = quill.container.getBoundingClientRect();
  indicator.style.top = `${Math.max(0, viewportY - containerRect.top)}px`;
}

function maybeAutoScroll(clientY) {
  const edge = 72;
  const amount = 14;
  if (clientY < edge) {
    window.scrollBy(0, -amount);
  } else if (clientY > window.innerHeight - edge) {
    window.scrollBy(0, amount);
  }
}

const FieldNoteRichEditor = forwardRef(function FieldNoteRichEditor(
  {
    value,
    onChange,
    disabled = false,
    small = false,
    placeholder = "Write here…",
    blocks = [],
    onBlockClick,
    onBlockPresenceChange,
  },
  ref
) {
  const hostRef = useRef(null);
  const quillRef = useRef(null);
  const initialValueRef = useRef(value || "<p><br></p>");
  const blocksRef = useRef(blocks);
  const disabledRef = useRef(disabled);
  const onChangeRef = useRef(onChange);
  const onBlockClickRef = useRef(onBlockClick);
  const onBlockPresenceChangeRef = useRef(onBlockPresenceChange);

  useEffect(() => {
    blocksRef.current = blocks;
    if (quillRef.current) syncBlockPreviews(quillRef.current, blocks);
  }, [blocks]);

  useEffect(() => {
    disabledRef.current = disabled;
    if (quillRef.current) {
      quillRef.current.enable(!disabled);
      quillRef.current.container?.classList.toggle("fieldnote-quill--disabled", disabled);
    }
  }, [disabled]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onBlockClickRef.current = onBlockClick;
  }, [onBlockClick]);

  useEffect(() => {
    onBlockPresenceChangeRef.current = onBlockPresenceChange;
  }, [onBlockPresenceChange]);

  useEffect(() => {
    let active = true;
    let rootClickHandler = null;
    let rootKeyHandler = null;
    let rootPointerDownHandler = null;
    let textChangeHandler = null;
    let blockMoveInProgress = false;
    let activeDrag = null;

    loadQuill()
      .then((Quill) => {
        if (!active || !hostRef.current || quillRef.current) return;

        registerFieldNoteBlock(Quill);

        const quill = new Quill(hostRef.current, {
          theme: "snow",
          placeholder,
          modules: {
            toolbar: disabledRef.current
              ? false
              : [
                  [{ header: [2, 3, false] }],
                  ["bold", "italic", "underline"],
                  [{ list: "ordered" }, { list: "bullet" }],
                  ["blockquote", "link"],
                  ["clean"],
                ],
          },
        });

        quill.clipboard.dangerouslyPasteHTML(
          initialValueRef.current || "<p><br></p>",
          "silent"
        );
        upgradeStoredMarkers(quill);
        syncBlockPreviews(quill, blocksRef.current);
        quill.enable(!disabledRef.current);
        quillRef.current = quill;

        const notifyPresence = () => {
          if (onBlockPresenceChangeRef.current) {
            onBlockPresenceChangeRef.current(getInsertedBlockKeys(quill));
          }
        };

        textChangeHandler = () => {
          if (blockMoveInProgress) return;
          syncBlockPreviews(quill, blocksRef.current);
          if (onChangeRef.current) onChangeRef.current(canonicalHtml(quill));
          notifyPresence();
        };
        quill.on("text-change", textChangeHandler);

        const activateBlock = (target) => {
          if (target?.closest?.(".fieldnote-quill-block__drag-handle")) return false;
          const node = target?.closest?.(".fieldnote-quill-block[data-block-key]");
          if (!node || !quill.root.contains(node)) return false;
          const key = node.getAttribute("data-block-key") || "";
          if (key && onBlockClickRef.current) onBlockClickRef.current(key);
          return true;
        };

        const finishDrag = (commit) => {
          if (!activeDrag) return;
          const drag = activeDrag;
          activeDrag = null;

          document.removeEventListener("pointermove", drag.moveHandler);
          document.removeEventListener("pointerup", drag.upHandler);
          document.removeEventListener("pointercancel", drag.cancelHandler);
          removeDropIndicator(quill);
          drag.node.classList.remove("fieldnote-quill-block--dragging");
          quill.container.classList.remove("fieldnote-quill--dragging");
          document.body.classList.remove("fieldnote-block-dragging");

          if (!commit || drag.targetIndex == null) return;

          const blot = Quill.find(drag.node);
          if (!blot) return;
          const originIndex = quill.getIndex(blot);
          let destinationIndex = drag.targetIndex;

          // The target was calculated while the dragged embed still existed.
          // Deleting an earlier embed shifts every later index left by one.
          if (destinationIndex > originIndex) destinationIndex -= 1;
          destinationIndex = Math.max(0, Math.min(destinationIndex, quill.getLength() - 1));

          if (destinationIndex === originIndex) return;

          try {
            quill.history?.cutoff?.();
            blockMoveInProgress = true;
            quill.deleteText(originIndex, 1, "user");
            quill.insertEmbed(destinationIndex, "fieldNoteBlock", drag.key, "user");
          } finally {
            blockMoveInProgress = false;
          }

          quill.history?.cutoff?.();
          syncBlockPreviews(quill, blocksRef.current);
          if (onChangeRef.current) onChangeRef.current(canonicalHtml(quill));
          notifyPresence();
          quill.setSelection(Math.min(destinationIndex + 1, quill.getLength() - 1), 0, "silent");

          const movedNode = Array.from(
            quill.root.querySelectorAll(".fieldnote-quill-block[data-block-key]")
          ).find((node) => node.getAttribute("data-block-key") === drag.key);
          if (movedNode) {
            movedNode.classList.add("fieldnote-quill-block--flash");
            window.setTimeout(
              () => movedNode.classList.remove("fieldnote-quill-block--flash"),
              900
            );
          }
        };

        rootPointerDownHandler = (event) => {
          const handle = event.target?.closest?.(".fieldnote-quill-block__drag-handle");
          if (!handle || disabledRef.current || event.button !== 0) return;

          const node = handle.closest(".fieldnote-quill-block[data-block-key]");
          if (!node || !quill.root.contains(node)) return;
          const key = node.getAttribute("data-block-key") || "";
          if (!key) return;

          event.preventDefault();
          event.stopPropagation();

          const indicator = createDropIndicator(quill);
          node.classList.add("fieldnote-quill-block--dragging");
          quill.container.classList.add("fieldnote-quill--dragging");
          document.body.classList.add("fieldnote-block-dragging");

          const drag = {
            node,
            key,
            targetIndex: null,
            moveHandler: null,
            upHandler: null,
            cancelHandler: null,
          };

          drag.moveHandler = (moveEvent) => {
            if (!activeDrag || moveEvent.pointerId !== event.pointerId) return;
            moveEvent.preventDefault();
            maybeAutoScroll(moveEvent.clientY);
            const target = getDropTarget(Quill, quill, node, moveEvent.clientY);
            if (!target) return;
            drag.targetIndex = target.index;
            positionDropIndicator(quill, indicator, target.viewportY);
          };
          drag.upHandler = (upEvent) => {
            if (upEvent.pointerId !== event.pointerId) return;
            upEvent.preventDefault();
            finishDrag(true);
          };
          drag.cancelHandler = (cancelEvent) => {
            if (cancelEvent.pointerId !== event.pointerId) return;
            finishDrag(false);
          };

          activeDrag = drag;
          const initialTarget = getDropTarget(Quill, quill, node, event.clientY);
          if (initialTarget) {
            drag.targetIndex = initialTarget.index;
            positionDropIndicator(quill, indicator, initialTarget.viewportY);
          }

          document.addEventListener("pointermove", drag.moveHandler, { passive: false });
          document.addEventListener("pointerup", drag.upHandler, { passive: false });
          document.addEventListener("pointercancel", drag.cancelHandler);
        };

        rootClickHandler = (event) => activateBlock(event.target);
        rootKeyHandler = (event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          if (activateBlock(event.target)) event.preventDefault();
        };
        quill.root.addEventListener("click", rootClickHandler);
        quill.root.addEventListener("keydown", rootKeyHandler);
        quill.root.addEventListener("pointerdown", rootPointerDownHandler);
        quill.container.classList.toggle("fieldnote-quill--disabled", disabledRef.current);

        notifyPresence();
      })
      .catch((error) => {
        console.error("Could not load the Field Notes editor:", error);
      });

    return () => {
      active = false;
      const quill = quillRef.current;
      if (activeDrag) {
        document.removeEventListener("pointermove", activeDrag.moveHandler);
        document.removeEventListener("pointerup", activeDrag.upHandler);
        document.removeEventListener("pointercancel", activeDrag.cancelHandler);
        document.body.classList.remove("fieldnote-block-dragging");
        activeDrag = null;
      }
      removeDropIndicator(quill);
      if (quill && textChangeHandler) quill.off("text-change", textChangeHandler);
      if (quill?.root && rootClickHandler) quill.root.removeEventListener("click", rootClickHandler);
      if (quill?.root && rootKeyHandler) quill.root.removeEventListener("keydown", rootKeyHandler);
      if (quill?.root && rootPointerDownHandler) quill.root.removeEventListener("pointerdown", rootPointerDownHandler);
    };
  }, [placeholder]);

  useImperativeHandle(ref, () => ({
    insertBlock(blockOrKey) {
      const q = quillRef.current;
      if (!q || disabledRef.current) return;
      const key = blockKeyFromInput(blockOrKey);
      if (!key) return;

      const range = q.getSelection(true);
      const index = range ? range.index : Math.max(0, q.getLength() - 1);
      q.insertEmbed(index, "fieldNoteBlock", key, "user");
      q.insertText(index + 1, "\n", "user");
      q.setSelection(index + 2, 0, "silent");

      const suppliedBlock = typeof blockOrKey === "object" ? blockOrKey : null;
      const previewBlocks = suppliedBlock
        ? [...blocksRef.current.filter((block) => block?.block_key !== key), suppliedBlock]
        : blocksRef.current;
      syncBlockPreviews(q, previewBlocks);
    },

    // Backwards-compatible alias for any existing caller.
    insertMarker(blockKey) {
      const q = quillRef.current;
      if (!q || disabledRef.current || !blockKey) return;
      const range = q.getSelection(true);
      const index = range ? range.index : Math.max(0, q.getLength() - 1);
      q.insertEmbed(index, "fieldNoteBlock", blockKey, "user");
      q.insertText(index + 1, "\n", "user");
      q.setSelection(index + 2, 0, "silent");
      syncBlockPreviews(q, blocksRef.current);
    },

    removeMarker(blockKey) {
      const q = quillRef.current;
      if (!q || disabledRef.current) return;
      const positions = [];
      let index = 0;

      (q.getContents()?.ops || []).forEach((op) => {
        const insert = op?.insert;
        if (insert && typeof insert === "object") {
          const value = insert.fieldNoteBlock;
          const key = typeof value === "string" ? value : String(value?.key || "");
          if (key === blockKey) positions.push(index);
          index += 1;
          return;
        }
        index += typeof insert === "string" ? insert.length : 0;
      });

      positions.reverse().forEach((position) => q.deleteText(position, 1, "user"));
    },

    focusBlock(blockKey) {
      const q = quillRef.current;
      if (!q?.root) return;
      const nodes = Array.from(q.root.querySelectorAll(".fieldnote-quill-block[data-block-key]"));
      const node = nodes.find((candidate) => candidate.getAttribute("data-block-key") === blockKey);
      if (!node) return;
      node.scrollIntoView({ behavior: "smooth", block: "center" });
      node.classList.remove("fieldnote-quill-block--flash");
      window.requestAnimationFrame(() => {
        node.classList.add("fieldnote-quill-block--flash");
        window.setTimeout(() => node.classList.remove("fieldnote-quill-block--flash"), 1600);
      });
    },

    getHtml() {
      return quillRef.current ? canonicalHtml(quillRef.current) : value;
    },
  }));

  return (
    <div
      className={`fieldnote-writer-editor ${small ? "fieldnote-writer-editor--small" : ""}`}
      ref={hostRef}
    />
  );
});

export default FieldNoteRichEditor;
